import { TranslateService } from '@ngx-translate/core';

import { ContextRelation, EntityRef, WarningOccurrence } from './warning.model';

/** Ordered so the reference that situates the problem reads before the ones that explain it. */
const RELATION_ORDER: readonly ContextRelation[] = ['CULPRIT', 'REGULATOR', 'LANDMARK'];

/**
 * Wire entity type to the label that names *one* entity of it in the active language —
 * the singular, since every reference here names a single entity and a list of them is
 * several such labels joined. A caller holding the dataspace's entity metadata passes it;
 * the others render the wire type, which is still readable.
 */
export type EntityTypeLabels = ReadonlyMap<string, string>;

/**
 * The `features.plan.warnings.*` keys are domain-generic — three relation names and a
 * name/uid pattern — despite the plan-scoped key path, so every surface that renders a
 * warning reference reads them from here rather than re-deriving the wording.
 */
export function refLabel(
  ref: Pick<EntityRef, 'entityType' | 'uid'>,
  translate: TranslateService,
  typeLabels?: EntityTypeLabels
): string {
  const type = typeLabels?.get(ref.entityType) ?? ref.entityType;
  // The quoting convention differs per language, so the whole pattern is a
  // translation rather than a template literal assembled here.
  return ref.uid === null
    ? translate.instant('features.plan.warnings.unnamed_entity', { type })
    : translate.instant('features.plan.warnings.named_entity', { type, uid: ref.uid });
}

export function relationLabel(
  relation: ContextRelation,
  translate: TranslateService
): string {
  return translate.instant(`features.plan.warnings.relation.${relation}`);
}

/**
 * A warning occurrence as plain text. Callers that render a warning into a string — a
 * notification, a table-row error message, the copy-to-clipboard button — all display
 * exactly this and nothing else, so it stays a one-line passthrough rather than a
 * formatter with its own opinions about what else to append.
 */
export function occurrenceText(occurrence: Pick<WarningOccurrence, 'body'>): string {
  return occurrence.body;
}

export interface DistinctRef {
  label: string;
  entityType: string;
  /** `null` when the entity is unnamed: renderable, but nothing to open. */
  uid: string | null;
  relations: ContextRelation[];
}

/**
 * One entry per entity rather than per relation, so an entity holding two roles — a
 * definitional finding names the same job as culprit and regulator — is named once. The
 * roles it holds travel with it, for a caller that discloses them out of line.
 */
export function distinctRefs(
  refs: EntityRef[],
  translate: TranslateService,
  typeLabels?: EntityTypeLabels
): DistinctRef[] {
  const byEntity = new Map<string, DistinctRef>();
  for (const relation of RELATION_ORDER) {
    for (const ref of refs.filter((candidate) => candidate.relation === relation)) {
      const key = `${ref.entityType}:${ref.uid}`;
      const entry = byEntity.get(key);
      if (entry) {
        if (!entry.relations.includes(relation)) entry.relations.push(relation);
      } else {
        byEntity.set(key, {
          label: refLabel(ref, translate, typeLabels),
          entityType: ref.entityType,
          uid: ref.uid,
          relations: [relation]
        });
      }
    }
  }
  return [...byEntity.values()];
}

export interface RelationGroup {
  relation: ContextRelation;
  refs: DistinctRef[];
}

/**
 * Distinct entities bucketed by role, one bucket per relation. `distinctRefs` walks
 * `RELATION_ORDER`, so `relations[0]` is already the highest-priority role an entity
 * holds and an entity holding two of them lands in exactly one bucket.
 */
export function groupRefsByRelation(refs: DistinctRef[]): RelationGroup[] {
  return RELATION_ORDER.map((relation) => ({
    relation,
    refs: refs.filter((entry) => entry.relations[0] === relation)
  })).filter((group) => group.refs.length > 0);
}
